import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import { filtersToQueryString, initialFilters } from 'helpers/filtering';
import { BOOKING_COLUMNS } from 'config/teleconsultation';
import { updateCustomer } from 'api/customers';
import {
  getAllTeleconsultations,
  updateTeleconsultation,
} from 'api/teleconsultations';
import { getAllNurses } from 'api/nurses';
import { setNurseOptions } from 'helpers/nurse';
import { runUpdater } from 'updaters/teleconsultation';
import MainViewWithTable from 'components/main_view_with_table';
import TeleconsultationButtons from 'components/teleconsultation_buttons';
import { sanitize } from 'helpers/customer';

const NurseTeleconsultationPage = ({ onCountUpdate, onMessages }) => {
  const [meta, setMeta] = useState({});
  const [fetching, setFetching] = useState(true);
  const [allTeles, setAllTeles] = useState([]);
  const [columns] = useState(BOOKING_COLUMNS);

  const fetchData = useCallback(async (page, filters = {}, search) => {
    filters['stage'] = ['fake', 'nurse'];

    const filtersString = filtersToQueryString(filters, search);
    const data = await getAllTeleconsultations(page, filtersString);

    setAllTeles([...data.items]);
    setMeta(data.meta);
    setFetching(false);
  }, []);

  useEffect(() => {
    (async () => {
      const allNurses = await getAllNurses();
      const { columns: newColumns } = setNurseOptions({
        columns,
        data: allNurses,
      });

      const initFilters = initialFilters(newColumns);
      await fetchData(1, initFilters);

      setFetching(false);
    })();
  }, [columns, fetchData]);

  const handleSelect = async (prop, value, { id }) => {
    let updatedTeleconsultation;
    const index = allTeles.findIndex((t) => t.id === id);

    if (
      ['reachOut', 'status', 'nextAction', 'cancellationReason'].includes(prop)
    ) {
      allTeles[index][prop] = value;
      await runUpdater(allTeles, allTeles[index], columns, prop, value, index);

      const {
        teleconsultation,
        meta: { messages },
      } = await updateTeleconsultation(
        id,
        {
          ...allTeles[index],
          customer: allTeles[index].customer && {
            id: allTeles[index].customer.id,
          },
        },
        { showMessage: false },
      );
      updatedTeleconsultation = teleconsultation;
      onMessages(messages);
    } else if (prop === 'nurse') {
      allTeles[index].nurse = value || null;
      const {
        teleconsultation,
        meta: { messages },
      } = await updateTeleconsultation(
        id,
        {
          ...allTeles[index],
          customer: allTeles[index].customer && {
            id: allTeles[index].customer.id,
          },
        },
        { showMessage: false },
      );
      updatedTeleconsultation = teleconsultation;
      onMessages(messages);
    } else {
      updatedTeleconsultation = allTeles[index];

      updatedTeleconsultation.customer[prop] = value;
      await updateCustomer(
        updatedTeleconsultation.customer.id,
        sanitize(updatedTeleconsultation.customer),
      );
    }

    allTeles[index] = updatedTeleconsultation;

    setAllTeles([...allTeles]);
    onCountUpdate();
  };

  const handleTextUpdate = (value, row) => handleSelect('notes', value, row);

  const props = {
    data: allTeles,
    columns,
    fetchData,
    fetching,
    meta,
    onSelect: handleSelect,
    onTextUpdate: handleTextUpdate,
    title: 'Nurse teleconsultations',
    accessor: 'nurse_teleconsultations',
  };

  return (
    <MainViewWithTable {...props}>
      <TeleconsultationButtons />
    </MainViewWithTable>
  );
};

NurseTeleconsultationPage.propTypes = {
  onCountUpdate: PropTypes.func.isRequired,
  onMessages: PropTypes.func.isRequired,
};

export default NurseTeleconsultationPage;
